// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Trend page sample-flight graphs: chooses the focus signal + sample
// rows, picks the series keys for each sample row, formats card notes
// / score labels / evidence meta, derives detection windows from the
// stored trend-row event metadata (per-signal), caches + prewarms the
// per-(entry, signal) graph payload (points + series + windows),
// renders the sample-card grid, and draws each canvas (or an empty
// message). Includes openTrendSampleInExplorer which hands selection
// off to the Graph Explorer panel.
//
// Exposed as a factory because the cluster reads/writes ~10 mutable
// app.js globals (graph cache + inflight Maps, two tokens, signature
// + health-key + cylinder selection) and depends on ~25 hoisted
// helpers / factory destructures.

export function createTrendSampleGraphs({
  escapeHtml,
  detectionWindow,
  cylinderKeys,
  formatCompactElapsedTime,
  findingCylinderReference,
  readableProbeLabel,
  formatTrendValue,
  buildTrendSignalSummaries,
  selectedTrendSignal,
  parseFlightEntry,
  trimEndpointSamples,
  calculateTrendFlightSummaryOffThread,
  aircraftProfileForEntry,
  visibleFlightEntries,
  waitForBrowserIdle,
  waitForUiFrame,
  isSupportedFlight,
  trendHealthKeyForSignalKey,
  detectionWindowsForHealthKey,
  selectCompanionSeriesForHealthCard,
  renderHealthSummary,
  activateView,
  selectReportPanel,
  updateHealthCardSelection,
  scheduleDrawAll,
  loadFlightLog,
  chartBounds,
  drawEmptyChartMessage,
  drawGrid,
  drawLine,
  drawInlineLegend,
  drawSelectedDetectionWindow,
  drawDetectionWindowLabel,
  scoreClass,
  seriesMeta,
  colors,
  trendCacheSchemaVersion,
  trendMetricMeta,
  trendSampleGraphCache,
  trendSampleGraphInflight,
  getFlightManifest,
  getActiveTrendMetric,
  getDetectionSensitivity,
  getSelectedFlight,
  getHealthSummary,
  getTrendSampleGraphToken,
  setTrendSampleGraphToken,
  getTrendSampleGraphPrewarmToken,
  setTrendSampleGraphPrewarmToken,
  getRenderedTrendSampleSignature,
  setRenderedTrendSampleSignature,
  setSelectedHealthKey,
  setSelectedCylinderNumber,
}) {
  function trendSampleSignal(rows) {
    if (getActiveTrendMetric() === "overallScore") return buildTrendSignalSummaries(rows)[0] ?? null;
    const signal = selectedTrendSignal(rows);
    return signal?.flaggedCount ? signal : null;
  }

  function trendSampleRowsForSignal(signal) {
    if (!signal) return [];
    const rows = [];
    const seen = new Set();
    const addRows = (items) => {
      [...(items ?? [])].reverse().forEach((row) => {
        if (!row?.id || seen.has(row.id)) return;
        seen.add(row.id);
        rows.push(row);
      });
    };
    addRows(signal.recentFlaggedRows);
    addRows(signal.flaggedRows);
    return rows.slice(0, 5);
  }

  function trendSampleCylinderSeriesKey(prefix, cylinder) {
    return Number.isFinite(cylinder) ? `${prefix}${cylinder}` : "";
  }

  function trendSeriesKeyExists(points, key) {
    return Boolean(key) && points.some((point) => Number.isFinite(point[key]));
  }

  function trendSampleSeriesForRow(signal, row, points) {
    const series = [];
    const add = (key) => {
      if (!key || series.includes(key) || !trendSeriesKeyExists(points, key)) return;
      series.push(key);
    };
    const addCylinderPair = (cylinder) => {
      add(trendSampleCylinderSeriesKey("egt", cylinder));
      add(trendSampleCylinderSeriesKey("cht", cylinder));
    };
    const addCylinderEgt = (cylinder) => {
      add(trendSampleCylinderSeriesKey("egt", cylinder));
    };
    const addThermalPeers = (prefix, limit = 6) => {
      cylinderKeys(prefix).slice(0, limit).forEach(add);
    };
    const key = signal?.key;
    if (key === "sensorScore") {
      const issueKey = row.sensorIssues?.[0]?.key || row.sensorKey;
      add(issueKey);
      const match = String(issueKey ?? "").match(/^(egt|cht)(\d+)$/i);
      if (match) add(`${match[1].toLowerCase() === "egt" ? "cht" : "egt"}${match[2]}`);
    } else if (key === "chtScore" || key === "coolingScore" || key === "coolingRateScore") {
      add(trendSampleCylinderSeriesKey("cht", row.coolingRateCylinder ?? row.coolingCylinder ?? row.hottestCylinder));
      addThermalPeers("cht", 4);
    } else if (key === "egtScore" || key === "valveScore" || key === "descentEgtDropoutScore") {
      add(trendSampleCylinderSeriesKey("egt", row.valveCylinder ?? row.descentEgtDropoutCylinder));
      addThermalPeers("egt", 4);
    } else if (key === "combustionScore") {
      addCylinderPair(row.combustionCylinder);
    } else if (key === "divergenceScore") {
      (row.divergenceCylinders ?? []).slice(0, 4).forEach(addCylinderEgt);
      addThermalPeers("egt", 4);
    } else if (key === "inductionLeakScore") {
      ["fuelFlow", "map", "rpm"].forEach(add);
      addThermalPeers("egt", 4);
    } else if (key === "magScore") {
      add(trendSampleCylinderSeriesKey("egt", row.magCylinder));
      addThermalPeers("egt", 4);
    } else if (key === "carbIceScore") {
      ["carbTemp", "oat", "rpm", "map", "fuelFlow"].forEach(add);
    } else if (key === "chargingScore" || key === "electricalScore") {
      ["buss", "amps", "amps1", "amps2"].forEach(add);
    } else if (key === "oilSystemScore") {
      ["oilPressure", "oilPressure1", "oilPressure2", "oilTemp", "oilTemp1", "oilTemp2", "rpm"].forEach(add);
    } else if (key === "turboScore") {
      ["map", "tit", "tit1", "tit2", "upperDeckPressure", "fuelFlow", "rpm"].forEach(add);
    }
    if (!series.length) {
      addThermalPeers("egt", 4);
      addThermalPeers("cht", 4);
    }
    return series.slice(0, 6);
  }

  function trendSampleCardNote(signal, row) {
    const affected = signal?.affected ? ` · ${signal.affected}` : "";
    return `${signal?.label ?? "Selected area"}${affected}`;
  }

  function trendSampleScoreLabel(signal, row) {
    const key = signal?.key;
    if (!key) return "Score --";
    const value = formatTrendValue(row?.[key], key);
    return `Score ${value}`;
  }

  function setTrendSampleEvidenceMeta({ sampleCount = 0, flaggedCount = 0, windowCount = null } = {}) {
    const count = document.querySelector("#trendSampleCount");
    const windows = document.querySelector("#trendSampleWindowCount");
    if (count) {
      count.textContent = sampleCount
        ? `${sampleCount} representative flight${sampleCount === 1 ? "" : "s"}`
        : "No sample flights";
    }
    if (windows) {
      if (Number.isFinite(windowCount)) {
        windows.textContent = windowCount
          ? `${windowCount} graph window${windowCount === 1 ? "" : "s"}`
          : "No graph windows";
      } else {
        windows.textContent = flaggedCount
          ? `${flaggedCount} flagged flight${flaggedCount === 1 ? "" : "s"}`
          : "Selected area clear";
      }
    }
  }

  function trendSampleDetectionWindows(signal, summary) {
    const healthKey = trendHealthKeyForSignalKey(signal?.key);
    if (!healthKey || !summary?.[healthKey]) return [];
    return detectionWindowsForHealthKey(healthKey, summary[healthKey]).slice(0, 6);
  }

  function trendSampleEventWindows(events, key, labelForEvent, optionsForEvent = () => ({})) {
    return (Array.isArray(events) ? events : [])
      .filter((event) => Number.isFinite(event?.startTime))
      .slice(0, 6)
      .map((event, index, visibleEvents) => {
        const startTime = event.reviewStartTime ?? event.clusterStartTime ?? event.startTime;
        const endTime = event.reviewEndTime ?? event.clusterEndTime ?? event.endTime;
        return detectionWindow(startTime, endTime, labelForEvent(event, index, visibleEvents), key, optionsForEvent(event));
      })
      .filter(Boolean);
  }

  function trendSampleStoredDetectionWindows(row, signal) {
    const key = signal?.key;
    if (!row || !key) return [];
    if (key === "divergenceScore") {
      const events = row.divergenceEvents?.length
        ? row.divergenceEvents
        : [{ startTime: row.divergenceStartTime, endTime: row.divergenceEndTime, cylinders: row.divergenceCylinders }];
      return trendSampleEventWindows(events, "divergence", (event, index, visibleEvents) => (
        `${visibleEvents.length > 1 ? `Divergence ${index + 1}` : "Divergence"} ${formatCompactElapsedTime(event.startTime)}-${formatCompactElapsedTime(event.endTime)}`
      ), (event) => ({
        channelKey: Number.isFinite(event.cylinder) ? `egt${event.cylinder}` : null,
        sourceCylinder: event.cylinder,
        sourceCylinders: event.cylinders,
      }));
    }
    if (key === "descentEgtDropoutScore") {
      const events = row.descentEgtDropoutEvents?.length
        ? row.descentEgtDropoutEvents
        : [{ startTime: row.descentEgtDropoutStartTime, endTime: row.descentEgtDropoutEndTime, cylinder: row.descentEgtDropoutCylinder, dropoutF: row.descentEgtDropoutF }];
      return trendSampleEventWindows(events, "descentEgtDropout", (event, index, visibleEvents) => {
        const cylinder = Number.isFinite(event.cylinder) ? ` ${findingCylinderReference(event.cylinder).label}` : "";
        return `${visibleEvents.length > 1 ? `EGT dropout ${index + 1}` : "EGT dropout"}${cylinder} ${formatCompactElapsedTime(event.startTime)}-${formatCompactElapsedTime(event.endTime)}`;
      }, (event) => ({
        channelKey: Number.isFinite(event.cylinder) ? `egt${event.cylinder}` : null,
        sourceCylinder: event.cylinder,
      }));
    }
    if (key === "coolingScore") {
      const cylinder = row.coolingCylinder;
      const channelKey = Number.isFinite(cylinder) ? `cht${cylinder}` : null;
      const channelLabel = channelKey ? (readableProbeLabel(channelKey) || `CHT${cylinder}`) : "CHT";
      return [detectionWindow(row.coolingStartTime, row.coolingEndTime, `Cooling ${formatCompactElapsedTime(row.coolingStartTime)}-${formatCompactElapsedTime(row.coolingEndTime)}`, "cooling", {
        channelKey,
        channelLabel,
        sourceCylinder: cylinder,
        value: row.coolingP95Cht,
        valueSuffix: " F",
        valueDigits: 0,
      })].filter(Boolean);
    }
    if (key === "coolingRateScore") {
      const events = row.coolingRateEvents?.length
        ? row.coolingRateEvents
        : [{ startTime: row.coolingRateStartTime, endTime: row.coolingRateEndTime, cylinder: row.coolingRateCylinder, rateFpm: row.coolingRateFpm, key: Number.isFinite(row.coolingRateCylinder) ? `cht${row.coolingRateCylinder}` : null }];
      return trendSampleEventWindows(events, "coolingRate", (event) => {
        const channelLabel = readableProbeLabel(event.key) || event.label || "CHT";
        return `${channelLabel} cooling ${Math.round(event.rateFpm ?? 0)} F/min`;
      }, (event) => ({
        channelKey: event.key,
        channelLabel: readableProbeLabel(event.key) || event.label || "CHT",
        sourceCylinder: event.cylinder,
        value: event.rateFpm,
        valueSuffix: " F/min",
        valueDigits: 0,
      }));
    }
    if (key === "combustionScore") {
      const channelKey = Number.isFinite(row.combustionCylinder) ? `egt${row.combustionCylinder}` : null;
      return [detectionWindow(row.combustionStartTime, row.combustionEndTime, `${row.combustionLabel ?? "CHT/EGT pairing"} ${formatCompactElapsedTime(row.combustionStartTime)}-${formatCompactElapsedTime(row.combustionEndTime)}`, "combustion", {
        channelKey,
        sourceCylinder: row.combustionCylinder,
      })].filter(Boolean);
    }
    if (key === "inductionLeakScore") {
      const channelKey = row.inductionLeakChannelKey ?? (Number.isFinite(row.inductionLeakCylinder) ? `egt${row.inductionLeakCylinder}` : null);
      return [detectionWindow(row.inductionLeakStartTime, row.inductionLeakEndTime, `Induction leak cue ${formatCompactElapsedTime(row.inductionLeakStartTime)}-${formatCompactElapsedTime(row.inductionLeakEndTime)}`, "inductionLeak", {
        channelKey,
        channelLabel: readableProbeLabel(channelKey) || channelKey,
        sourceCylinder: row.inductionLeakCylinder,
        value: row.inductionLeakCylinderEgtChange ?? row.inductionLeakEgtChange,
        valueSuffix: " F",
        valueDigits: 0,
      })].filter(Boolean);
    }
    if (key === "chargingScore") {
      if (!Number.isFinite(row.chargingStartTime)) return [];
      return [detectionWindow(row.chargingStartTime - 15, row.chargingEndTime ?? row.chargingStartTime + 180, `Start charge ${row.chargingStatus ?? "not confirmed"} ${formatCompactElapsedTime(row.chargingStartTime)}`, "charging")].filter(Boolean);
    }
    if (key === "oilSystemScore") {
      return trendSampleEventWindows(row.oilSystemWindows, "oilSystem", (event, index, visibleEvents) => (
        `${visibleEvents.length > 1 ? `Oil system ${index + 1}` : "Oil system"} ${event.label ?? ""} ${formatCompactElapsedTime(event.startTime)}-${formatCompactElapsedTime(event.endTime)}`
      ), (event) => ({
        channelKey: event.key,
        channelLabel: event.channelLabel ?? readableProbeLabel(event.key) ?? event.key,
        value: event.worstValue,
        valueSuffix: event.kind === "pressure" ? " psi" : " F",
        valueDigits: 0,
      }));
    }
    return [];
  }

  function trendSampleGraphCacheKey(entry, signal) {
    // sourceHash and normalizedArtifactId tie DB-backed entries to the
    // specific artifact whose points went into the cached payload — a
    // re-ingest mints a new normalizedArtifactId so the old payload is
    // evicted instead of serving stale points. Mirrors the pattern in
    // parsedFlightEntryCacheKey (commit 8aff4319).
    return [
      trendCacheSchemaVersion,
      getDetectionSensitivity(),
      signal?.key ?? "none",
      entry.id,
      entry.path,
      entry.parser,
      entry.sourceHash ?? "",
      entry.database?.normalizedArtifactId ?? "",
    ].join("|");
  }

  function setTrendSampleGraphCache(key, payload) {
    trendSampleGraphCache.set(key, payload);
    if (trendSampleGraphCache.size <= 64) return;
    const oldest = trendSampleGraphCache.keys().next().value;
    if (oldest) trendSampleGraphCache.delete(oldest);
  }

  function hasTrendSampleGraphPayload(entry, signal) {
    return trendSampleGraphCache.has(trendSampleGraphCacheKey(entry, signal));
  }

  function trendSampleGraphPayloadsReady(samples, signal) {
    const entriesById = new Map(visibleFlightEntries().map((entry) => [entry.id, entry]));
    return samples.every((row) => {
      const entry = entriesById.get(row.id);
      return entry && hasTrendSampleGraphPayload(entry, signal);
    });
  }

  async function trendSampleGraphPayload(entry, row, signal) {
    const key = trendSampleGraphCacheKey(entry, signal);
    const cached = trendSampleGraphCache.get(key);
    if (cached) return cached;
    const inflight = trendSampleGraphInflight.get(key);
    if (inflight) return inflight;
    const request = (async () => {
      const storedWindows = trendSampleStoredDetectionWindows(row, signal);
      const parsedPoints = await parseFlightEntry(entry);
      const { points } = trimEndpointSamples(parsedPoints);
      // No stored windows means deriving them from a fresh summary of a
      // NON-current flight — route through the off-thread path (persistent
      // bundle cache, then Web Worker) so the ~2s detector pass never blocks
      // the main thread; it only falls back to the sync pass when the worker
      // is unavailable.
      const summary = storedWindows.length ? null : await calculateTrendFlightSummaryOffThread(points, {
        selectedEntry: entry,
        aircraftProfile: aircraftProfileForEntry(entry),
      });
      const payload = {
        points,
        series: trendSampleSeriesForRow(signal, row, points),
        windows: storedWindows.length ? storedWindows : trendSampleDetectionWindows(signal, summary),
      };
      setTrendSampleGraphCache(key, payload);
      return payload;
    })();
    trendSampleGraphInflight.set(key, request);
    try {
      return await request;
    } finally {
      trendSampleGraphInflight.delete(key);
    }
  }

  function scheduleTrendSampleGraphPrewarm(rows, { delayMs = 3000 } = {}) {
    const signals = buildTrendSignalSummaries(rows);
    if (!signals.length) return;
    const token = setTrendSampleGraphPrewarmToken(getTrendSampleGraphPrewarmToken() + 1);
    const run = async () => {
      if (token !== getTrendSampleGraphPrewarmToken()) return;
      await waitForBrowserIdle(5000);
      if (token !== getTrendSampleGraphPrewarmToken()) return;
      prewarmTrendSampleGraphPayloads(rows, signals, token);
    };
    window.setTimeout(run, delayMs);
  }

  async function prewarmTrendSampleGraphPayloads(rows, signals, token) {
    const entriesById = new Map(visibleFlightEntries().map((entry) => [entry.id, entry]));
    const selectedSignal = getActiveTrendMetric() === "overallScore" ? null : selectedTrendSignal(rows);
    const orderedSignals = [
      selectedSignal?.flaggedCount ? selectedSignal : null,
      ...signals,
    ].filter(Boolean).slice(0, 3);
    const seen = new Set();
    let warmed = 0;
    const maxPrewarmPayloads = 4;
    for (const signal of orderedSignals) {
      const signalKey = signal.key;
      if (seen.has(signalKey)) continue;
      seen.add(signalKey);
      for (const row of trendSampleRowsForSignal(signal)) {
        if (token !== getTrendSampleGraphPrewarmToken()) return;
        if (warmed >= maxPrewarmPayloads) return;
        const entry = entriesById.get(row.id);
        if (!entry) continue;
        if (hasTrendSampleGraphPayload(entry, signal)) continue;
        try {
          await trendSampleGraphPayload(entry, row, signal);
          warmed += 1;
        } catch {
          // Ignore prewarm misses; foreground rendering will show the normal empty/error state.
        }
        await waitForUiFrame(32);
      }
    }
  }

  function renderTrendSampleGraphs(rows) {
    const container = document.querySelector("#trendSampleGraphs");
    const title = document.querySelector("#trendChartTitle");
    const intro = document.querySelector("#trendSampleIntro");
    if (!container) return;
    const token = setTrendSampleGraphToken(getTrendSampleGraphToken() + 1);
    const signal = trendSampleSignal(rows);
    const samples = trendSampleRowsForSignal(signal);
    const activeMetric = getActiveTrendMetric();
    const selectedFlight = getSelectedFlight();
    const signature = [
      trendCacheSchemaVersion,
      getDetectionSensitivity(),
      activeMetric,
      signal?.key ?? "none",
      selectedFlight?.id ?? "none",
      samples.map((row) => `${row.id}:${row[signal?.key] ?? ""}`).join(","),
    ].join("|");
    const metricLabel = signal?.label ?? trendMetricMeta[activeMetric]?.label ?? "Selected area";
    if (title) title.textContent = signal ? `${metricLabel}: sample flights` : "Sample flights";
    if (intro) {
      intro.textContent = signal
        ? "These are representative flagged flights for the selected area. Lines are scaled to fit each card so the shape is easy to compare."
        : "No repeating item is currently flagged for this area, so there are no offending sample flights to show.";
    }
    setTrendSampleEvidenceMeta({ sampleCount: samples.length, flaggedCount: signal?.flaggedCount ?? 0 });
    if (!samples.length) {
      setRenderedTrendSampleSignature(signature);
      container.innerHTML = `<p class="trend-sample-empty">No flagged sample flights for this area. Choose another metric to see the raw traces behind its trend.</p>`;
      return;
    }
    if (signature === getRenderedTrendSampleSignature() && container.querySelectorAll(".trend-sample-card canvas").length === samples.length) return;
    setRenderedTrendSampleSignature(signature);
    container.innerHTML = samples.map((row, index) => `
      <article class="trend-sample-card">
        <header>
          <span>
            <strong>${escapeHtml(row.dateLabel)} · ${escapeHtml(row.title)}</strong>
            <small>${escapeHtml(trendSampleCardNote(signal, row))}</small>
          </span>
          <span class="trend-sample-actions">
            <span class="trend-sample-score ${scoreClass(row[signal?.key])}">${escapeHtml(trendSampleScoreLabel(signal, row))}</span>
            <button class="secondary-action trend-sample-open" data-trend-sample-open="${escapeHtml(row.id)}" type="button">Open in Graph Explorer</button>
          </span>
        </header>
        <canvas class="trend-sample-canvas" data-trend-sample-index="${index}" aria-label="Sample graph for ${escapeHtml(row.title)}"></canvas>
      </article>
    `).join("");
    container.querySelectorAll("[data-trend-sample-open]").forEach((button) => {
      button.addEventListener("click", () => openTrendSampleInExplorer(button.dataset.trendSampleOpen, signal));
    });
    // Resolves once the visible sample graphs have actually drawn (each one
    // parses its sample flight) so the trends loader can wait this out instead
    // of clearing and leaving the page janky while charts fill in.
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        if (token !== getTrendSampleGraphToken()) { resolve(); return; }
        drawTrendSampleGraphsAsync(samples, signal, token).then(resolve, resolve);
      });
    });
  }

  async function openTrendSampleInExplorer(id, signal) {
    const entry = visibleFlightEntries().find((flight) => flight.id === id) ?? getFlightManifest().find((flight) => flight.id === id);
    const healthKey = trendHealthKeyForSignalKey(signal?.key);
    if (!entry || !isSupportedFlight(entry) || !healthKey) return;
    setSelectedHealthKey(healthKey);
    setSelectedCylinderNumber(null);
    selectCompanionSeriesForHealthCard(healthKey);
    activateView("dashboard");
    selectReportPanel("explorer");
    updateHealthCardSelection();
    scheduleDrawAll();
    await waitForUiFrame();
    await loadFlightLog(entry, { activateTrial: true });
    setSelectedHealthKey(healthKey);
    setSelectedCylinderNumber(null);
    selectCompanionSeriesForHealthCard(healthKey);
    const healthSummary = getHealthSummary();
    if (healthSummary) renderHealthSummary(healthSummary);
    activateView("dashboard");
    selectReportPanel("explorer");
    updateHealthCardSelection();
    scheduleDrawAll();
  }

  async function drawTrendSampleGraphsAsync(samples, signal, token) {
    const entriesById = new Map(visibleFlightEntries().map((entry) => [entry.id, entry]));
    const tasks = samples.map((row, index) => ({
      row,
      canvas: document.querySelector(`[data-trend-sample-index="${index}"]`),
      entry: entriesById.get(row.id),
    }));
    const results = await Promise.all(tasks.map(async ({ row, canvas, entry }) => {
      if (token !== getTrendSampleGraphToken()) return null;
      if (!canvas) return null;
      const card = canvas.closest(".trend-sample-card");
      if (!entry) {
        card?.remove();
        return null;
      }
      try {
        const payload = await trendSampleGraphPayload(entry, row, signal);
        if (token !== getTrendSampleGraphToken()) return null;
        if (!payload.windows.length) {
          card?.remove();
          return null;
        }
        drawTrendSampleGraph(canvas, payload.points, payload.series, payload.windows);
        return { windows: payload.windows.length };
      } catch (error) {
        card?.remove();
        return null;
      }
    }));
    if (token !== getTrendSampleGraphToken()) return;
    const drawn = results.filter(Boolean);
    const visibleSamples = drawn.length;
    const visibleWindows = drawn.reduce((total, item) => total + item.windows, 0);
    const container = document.querySelector("#trendSampleGraphs");
    if (container && samples.length && !visibleSamples && !container.querySelector(".trend-sample-card")) {
      container.innerHTML = `<p class="trend-sample-empty">No flagged sample flights for this area have an identified graph window. Choose another metric to see the raw traces behind its trend.</p>`;
    }
    setTrendSampleEvidenceMeta({ sampleCount: visibleSamples, flaggedCount: signal?.flaggedCount ?? 0, windowCount: visibleWindows });
  }

  function prepareTrendSampleCanvas(canvas) {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    const width = Math.max(320, Math.round((rect.width || 320) * dpr));
    const height = Math.max(360, Math.round((rect.height || 220) * dpr));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    return canvas.getContext("2d");
  }

  function drawTrendSampleMessage(canvas, message) {
    const ctx = prepareTrendSampleCanvas(canvas);
    const bounds = chartBounds(canvas, 32, { leftPadding: 34, rightPadding: 12, topPadding: 54, bottomPadding: 28 });
    drawEmptyChartMessage(ctx, canvas, bounds, message);
  }

  function drawTrendSampleGraph(canvas, points, seriesKeys, windows = []) {
    const ctx = prepareTrendSampleCanvas(canvas);
    const bounds = chartBounds(canvas, 32, { leftPadding: 34, rightPadding: 12, topPadding: 54, bottomPadding: 32 });
    const series = seriesKeys
      .map((key) => ({ key, meta: seriesMeta[key] ?? { label: key, color: colors.blue } }))
      .filter(({ key }) => trendSeriesKeyExists(points, key));
    if (!points.length || !series.length) {
      drawEmptyChartMessage(ctx, canvas, bounds, "No trace values available");
      return;
    }
    drawGrid(ctx, canvas, bounds, []);
    drawSelectedDetectionWindow(ctx, points, bounds, windows);
    series.forEach(({ key, meta }, index) => {
      drawLine(ctx, points, key, meta, bounds, {
        color: meta.color,
        independentScale: true,
        width: index === 0 ? 2.4 : 1.7,
      });
    });
    const labels = series.slice(0, 4).map(({ meta }) => meta.label);
    const labelColors = series.slice(0, 4).map(({ meta }) => meta.color);
    drawInlineLegend(ctx, bounds, labels, labelColors);
    drawDetectionWindowLabel(ctx, points, bounds, windows);
  }

  return {
    trendSampleSignal,
    trendSampleRowsForSignal,
    trendSampleCylinderSeriesKey,
    trendSeriesKeyExists,
    trendSampleSeriesForRow,
    trendSampleCardNote,
    trendSampleScoreLabel,
    setTrendSampleEvidenceMeta,
    trendSampleDetectionWindows,
    trendSampleEventWindows,
    trendSampleStoredDetectionWindows,
    trendSampleGraphCacheKey,
    setTrendSampleGraphCache,
    hasTrendSampleGraphPayload,
    trendSampleGraphPayloadsReady,
    trendSampleGraphPayload,
    scheduleTrendSampleGraphPrewarm,
    prewarmTrendSampleGraphPayloads,
    renderTrendSampleGraphs,
    openTrendSampleInExplorer,
    drawTrendSampleGraphsAsync,
    prepareTrendSampleCanvas,
    drawTrendSampleMessage,
    drawTrendSampleGraph,
  };
}
